import React, { useEffect, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowLeft, MapPin, Plus, Minus } from 'lucide-react';
import { Navigation } from './Navigation';
export interface FAQItem {
  question: string;
  answer: string;
}
export interface BreadcrumbItem {
  name: string;
  item: string;
}
interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  metaDescription: string;
  schema: object;
  /** Optional FAQ list. When provided, renders an accessible FAQ section and injects FAQPage schema. */
  faqs?: FAQItem[];
  /** Optional breadcrumb trail. When provided, renders a visible breadcrumb nav and injects BreadcrumbList schema. */
  breadcrumbs?: BreadcrumbItem[];
}
export function ServicePageLayout({
  title,
  subtitle,
  children,
  metaDescription,
  schema,
  faqs,
  breadcrumbs
}: ServicePageLayoutProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | Mike's Pro Detail`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', metaDescription);
    // Set canonical link based on current path
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://www.fulldetailpro.com${window.location.pathname}`);
    // Build the schema graph: the page's Service/primary schema, plus
    // optional FAQPage and BreadcrumbList schemas.
    const graph: object[] = [schema];
    if (faqs && faqs.length > 0) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer
          }
        }))
      });
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      graph.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: b.item
        }))
      });
    }
    const scripts: HTMLScriptElement[] = graph.map(obj => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });
    return () => {
      scripts.forEach(s => {
        if (document.head.contains(s)) {
          document.head.removeChild(s);
        }
      });
    };
  }, [title, metaDescription, schema, faqs, breadcrumbs]);
  return <main className="min-h-screen w-full bg-black text-white selection:bg-red-600 selection:text-white" data-id="element-424" data-mp="36i4s4vri3t0e">
      <Navigation data-id="element-425" data-mp="3bgg010y8jtfz" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 border-b border-zinc-900 overflow-hidden" data-id="element-426" data-mp="2wm1pzpwnfudg">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-900/20 blur-[120px] pointer-events-none rounded-full" data-id="element-427" data-mp="31jjosngyct7q" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-428" data-mp="3pc0i5u6547mc">
          {/* Breadcrumb navigation */}
          {breadcrumbs && breadcrumbs.length > 0 ? <nav aria-label="Breadcrumb" className="mb-8" data-id="element-429" data-mp="3fknqb2kcdhgb">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-400" data-id="element-430" data-mp="3fi1id1tjgzxk">
                {breadcrumbs.map((b, i) => <li key={b.item} className="flex items-center gap-2" data-id="element-431" data-mp="c3dxask95ptq" data-mpi={i}>
                    {i < breadcrumbs.length - 1 ? <a href={b.item.replace('https://www.fulldetailpro.com', '')} className="hover:text-white transition-colors" data-id="element-432" data-mp="gu43g80i5wph">
                        {b.name}
                      </a> : <span className="text-gray-300" aria-current="page" data-id="element-433" data-mp="2pchwb52yfksk">
                        {b.name}
                      </span>}
                    {i < breadcrumbs.length - 1 && <span className="text-zinc-600" data-id="element-434" data-mp="2fe21os4pxteb">/</span>}
                  </li>)}
              </ol>
            </nav> : <a href="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors group" data-id="element-435" data-mp="2af7llxy4yixw">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-436" data-mp="380wjfha5ieo3" />
              Back to Home
            </a>}

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-437" data-mp="2ybd1qey6cqut">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6" data-id="element-438" data-mp="3qrw5a9mcn8d5">
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500 drop-shadow-lg" data-id="element-439" data-mp="3m1pfi6g3phtb">
                {title}
              </span>
            </h1>
            <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)] mb-6" data-id="element-440" data-mp="25rcuygwem8p" />
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl" data-id="element-441" data-mp="74ugceh8ckek">
              {subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-16 px-4" data-id="element-442" data-mp="h43bhanu052i">
        <div className="max-w-4xl mx-auto" data-id="element-443" data-mp="h31www7lx0g1">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h3:text-2xl prose-h3:text-yellow-400 prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-300 prose-strong:text-white" data-id="element-444" data-mp="l4wy80pjfdtt">
            {children}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      {faqs && faqs.length > 0 && <section className="py-16 px-4 border-t border-zinc-900" aria-labelledby="faq-heading" data-id="element-445" data-mp="14tns3mwiiq8m">
          <div className="max-w-4xl mx-auto" data-id="element-446" data-mp="f5idcrm77dvw">
            <h2 id="faq-heading" className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-8 text-center" data-id="element-447" data-mp="a65sgcew0gvy">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4" data-id="element-448" data-mp="owmt3tjp72rs">
              {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return <div key={index} className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden" data-id="element-449" data-mp="xuyqqfizf0tq" data-mpi={index}>
                    <button onClick={() => setOpenFaq(isOpen ? null : index)} className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-zinc-800/40 transition-colors" aria-expanded={isOpen} data-id="element-450" data-mp="svdgva29x828">
                      <span className="text-lg font-bold text-white" data-id="element-451" data-mp="12vw8ir02xsup">
                        {faq.question}
                      </span>
                      {isOpen ? <Minus className="w-5 h-5 text-red-500 shrink-0" data-id="element-452" data-mp="34lxyql72d8kv" /> : <Plus className="w-5 h-5 text-red-500 shrink-0" data-id="element-453" data-mp="34n9953v0leky" />}
                    </button>
                    {isOpen && <div className="px-5 pb-5 text-gray-300 leading-relaxed" data-id="element-454" data-mp="1k4p1xt81brg7">
                        {faq.answer}
                      </div>}
                  </div>;
          })}
            </div>
          </div>
        </section>}

      {/* Service Areas SEO Block */}
      <section className="py-12 px-4 bg-zinc-900/30 border-t border-zinc-900" data-id="element-455" data-mp="1fcf7q0zw0584">
        <div className="max-w-4xl mx-auto text-center" data-id="element-456" data-mp="11dnwcmcesxno">
          <MapPin className="w-8 h-8 text-red-500 mx-auto mb-4" data-id="element-457" data-mp="11c4cymvgi6ho" />
          <h2 className="text-2xl font-bold mb-4" data-id="element-458" data-mp="15g2awu91pb95">
            Proudly Serving Ventura County
          </h2>
          <p className="text-gray-400 leading-relaxed" data-id="element-459" data-mp="rgjfn21c1ue9">
            We provide top-tier mobile auto detailing in{' '}
            <a href="/ventura-county/camarillo" className="text-red-500 hover:underline" data-id="element-460" data-mp="wekxxkqxppoo">
              Camarillo
            </a>
            ,{' '}
            <a href="/ventura-county/oxnard" className="text-red-500 hover:underline" data-id="element-461" data-mp="2qsvn5ubfyluz">
              Oxnard
            </a>
            ,{' '}
            <a href="/ventura-county/ventura" className="text-red-500 hover:underline" data-id="element-462" data-mp="1dkzdcfz3ap03">
              Ventura
            </a>
            ,{' '}
            <a href="/ventura-county/thousand-oaks" className="text-red-500 hover:underline" data-id="element-463" data-mp="14sxkb2rcoazu">
              Thousand Oaks
            </a>
            ,{' '}
            <a href="/ventura-county/simi-valley" className="text-red-500 hover:underline" data-id="element-464" data-mp="ztucxbnwdsop">
              Simi Valley
            </a>
            ,{' '}
            <a href="/ventura-county/moorpark" className="text-red-500 hover:underline" data-id="element-465" data-mp="pvevk0eo9xse">
              Moorpark
            </a>
            ,{' '}
            <a href="/ventura-county/newbury-park" className="text-red-500 hover:underline" data-id="element-466" data-mp="lul6v4teoeyg">
              Newbury Park
            </a>
            , and{' '}
            <a href="/ventura-county/westlake-village" className="text-red-500 hover:underline" data-id="element-467" data-mp="2g8m7nv8inc16">
              Westlake Village
            </a>
            . Whether you need car detailing in Camarillo or mobile detailing in
            Ventura County, we come directly to your home or office.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden border-t border-zinc-900" data-id="element-468" data-mp="2npipcjlgplzz">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 to-black pointer-events-none" data-id="element-469" data-mp="2skohi05gjwwi" />
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-470" data-mp="2xjhmf46oifl2">
          <h2 className="text-4xl md:text-5xl font-black uppercase mb-6" data-id="element-471" data-mp="1zvhdwmr5ch4e">
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-472" data-mp="29s3u2y8j2k03">
            Book your professional mobile detailing appointment today. We bring
            the shop to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-473" data-mp="2drcqybyv6xzv">
            <a href="tel:8183819905" className="flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xl rounded-sm transition-all shadow-[0_0_20px_rgba(220,38,38,0.5)] hover:shadow-[0_0_30px_rgba(220,38,38,0.8)] hover:-translate-y-1 w-full sm:w-auto justify-center" data-id="element-474" data-mp="3l9c2dumaok9j">
              <Phone className="w-6 h-6" data-id="element-475" data-mp="1qx059etcvulf" />
              (818) 381-9905
            </a>
            <a href="/#contact-form" className="px-8 py-4 bg-white text-black hover:bg-gray-200 font-bold text-xl uppercase tracking-wider rounded-sm transition-all w-full sm:w-auto text-center" data-id="element-476" data-mp="1vs62h9s7kvot">
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>
    </main>;
}